import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import config from '../../../constants/apiConfig';
import { selectUserGuid } from '../../../slices/commonSelectors';
import { parseError } from 'utils/parseTypedErrorMessage';
import { noAutoClose } from 'assets/data/config';
import { IapScope, INCIDENT } from '../ICSConstants';
import { Agenda } from '../planningView/PlanningPage';
import {
  imhMeetingGroundRules,
  PLANNING_PHASES,
} from '../planningView/USCGPage';
import { useActualIncidentId } from './commonHooks';

function groundRulesMarkdown(): string {
  return [
    '**Ground Rules**',
    '',
    ...imhMeetingGroundRules.map((rule) => `- ${rule}`),
  ].join('\n');
}

/**
 * Calendar default text: Ground Rules, then Agenda as markdown lists (all
 * meeting types).
 */
function agendaToString(agenda: Agenda): string {
  const groundRulesBlock = groundRulesMarkdown();
  if (agenda.length === 0) {
    return `${groundRulesBlock}\n\n**Agenda**\n\n`;
  }
  const agendaBlock = [
    '**Agenda**',
    '',
    ...agenda.map(({ role, item }) => `- ${role}: ${item}`),
  ].join('\n');
  return `${groundRulesBlock}\n\n${agendaBlock}`;
}

export const eventTypes = PLANNING_PHASES.reduce(
  (acc, phase) => {
    acc.push({
      title: phase.title,
      code: phase.code,
      agenda: agendaToString(phase.agenda),
    });
    return acc;
  },
  [
    {
      title: 'Other',
      code: '#424242',
      agenda: agendaToString([]),
    },
  ]
);

export type IncidentCalendarDisplayEvent = {
  purpose: string;
  id: UUID;
  incidentId: UUID;
  start: Date;
  end: Date;
  title: string;
  externalAttendees: string;
  location: string;
  color: string;
  allDay?: boolean; // for now this will always be false
  internalAttendees?: string[];
  eventTypeLabel: string;
  isOnlineMeeting?: boolean;
  teamsJoinUrl?: string | null;
};

export type IncidentCalendarEvent = {
  color: string;
  purpose: string;
  id: UUID;
  incident_id: UUID;
  start_date_time: Date;
  end_date_time: Date;
  title: string;
  external_attendees: string;
  location: string;
  internal_attendees?: string[];
  is_online_meeting?: boolean;
  teams_join_url?: string | null;
};

export const keys = {
  base: ['incident_calendar_events'] as const,
  byIncidentAndUser: (incidentId: string, userId: string) =>
    [...keys.base, incidentId, userId] as const,
} as const;

// Fetch all calendar events
export const useIncidentCalendarEvents = (scope: IapScope = INCIDENT) => {
  const incidentId = useActualIncidentId();
  const userGuid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.byIncidentAndUser(incidentId, userGuid),
    queryFn: async () => {
      const resp = await axios.get<IncidentCalendarEvent[]>(
        `${config.apiGateway.ics_incident_calendar_events}/${incidentId}`
      );

      return resp.data.map(
        (event) =>
          ({
            title: event.title,
            purpose: event.purpose,
            id: event.id,
            incidentId: event.incident_id,
            start: new Date(event.start_date_time),
            end: new Date(event.end_date_time),
            externalAttendees: event.external_attendees,
            location: event.location,
            color: event.color,
            internalAttendees: event.internal_attendees || [],
            eventTypeLabel:
              eventTypes.find((type) => type.code === event.color)?.title ||
              'Other',
            isOnlineMeeting: event.is_online_meeting ?? false,
            teamsJoinUrl: event.teams_join_url ?? null,
          }) as IncidentCalendarDisplayEvent
      );
    },
    enabled: scope === INCIDENT,
  });
};

export const useEditIncidentCalendarEvent = () => {
  const incidentId = useActualIncidentId();
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: IncidentCalendarDisplayEvent) => {
      const resp = await axios.put<
        IncidentCalendarDisplayEvent & { warning?: string }
      >(
        `${config.apiGateway.ics_incident_calendar_events}/${incidentId}/${data.id}`,
        {
          external_attendees: data.externalAttendees || '',
          location: data.location || '',
          start_date_time: data.start,
          end_date_time: data.end,
          purpose: data.purpose,
          title: data.title,
          color: data.color,
          internal_attendees: data.internalAttendees || [],
          is_online_meeting: data.isOnlineMeeting ?? false,
          teams_join_url: data.teamsJoinUrl?.trim() || null,
        }
      );
      return resp.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: keys.byIncidentAndUser(incidentId, userGuid),
      });
      if (data?.warning) {
        toast.warning(data.warning, noAutoClose);
      }
    },
    onError: (error) => {
      toast.error(parseError(error) ?? 'Failed to update calendar event.');
    },
  });
};

export const useAddIncidentCalendarEvent = () => {
  const incidentId = useActualIncidentId();
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: IncidentCalendarDisplayEvent) => {
      const resp = await axios.post<
        IncidentCalendarDisplayEvent & { warning?: string }
      >(`${config.apiGateway.ics_incident_calendar_events}/${incidentId}`, {
        external_attendees: data.externalAttendees || '',
        location: data.location || '',
        start_date_time: data.start,
        end_date_time: data.end,
        purpose: data.purpose,
        title: data.title,
        color: data.color,
        internal_attendees: data.internalAttendees || [],
        is_online_meeting: data.isOnlineMeeting ?? false,
        teams_join_url: data.teamsJoinUrl?.trim() || null,
      });
      return resp.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: keys.byIncidentAndUser(incidentId, userGuid),
      });
      if (data?.warning) {
        toast.warning(data.warning, noAutoClose);
      }
    },
  });
};

export const useDeleteIncidentCalendarEvent = () => {
  const incidentId = useActualIncidentId();
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (eventId: UUID) => {
      const resp = await axios.delete<{ message: string; warning?: string }>(
        `${config.apiGateway.ics_incident_calendar_events}/${incidentId}/${eventId}`
      );
      return resp.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: keys.byIncidentAndUser(incidentId, userGuid),
      });
      if (data?.warning) {
        toast.warning(data.warning, noAutoClose);
      }
    },
    onError: (error) => {
      toast.error(parseError(error) ?? 'Failed to delete calendar event.');
    },
  });
};
